import React, { ComponentType } from "react";
import {
  useNavigate,
  useLocation,
  useParams,
  useSearchParams,
  NavigateFunction,
  Location,
  Params
} from "react-router-dom";

/**
 * Navigation props injected by withNavigate HOC.
 * This interface mirrors the props that withRouter provided in React Router v5.
 */
export interface NavigateProps {
  navigate: NavigateFunction;
  location: Location;
  params: Params<string>;
  searchParams: URLSearchParams;
  setSearchParams: ReturnType<typeof useSearchParams>[1];
}

/**
 * Legacy-compatible props that match React Router v5's withRouter.
 * Use this for gradual migration - provides both old and new APIs.
 */
export interface WithRouterProps extends NavigateProps {
  history: {
    push: (path: string, state?: unknown) => void;
    replace: (path: string, state?: unknown) => void;
    go: (delta: number) => void;
    goBack: () => void;
    goForward: () => void;
    location: Location;
  };
  match: {
    params: Params<string>;
    path: string;
    url: string;
  };
}

/**
 * Higher-Order Component that provides React Router v6 navigation to class components.
 * This replaces the deprecated withRouter HOC from React Router v5.
 *
 * @example
 * // Basic usage
 * class MyComponent extends React.Component<MyProps & NavigateProps> {
 *   handleClick = () => {
 *     this.props.navigate('/dashboard');
 *   };
 * }
 * export default withNavigate(MyComponent);
 *
 * @example
 * // With legacy compatibility (provides history.push, etc.)
 * class MyComponent extends React.Component<MyProps & WithRouterProps> {
 *   handleClick = () => {
 *     // Both work:
 *     this.props.history.push('/dashboard');
 *     this.props.navigate('/dashboard');
 *   };
 * }
 * export default withNavigate(MyComponent, { legacyMode: true });
 */
export function withNavigate<P extends object>(
  WrappedComponent: ComponentType<P>,
  options?: { legacyMode?: boolean }
) {
  const WithNavigateComponent = (props: Omit<P, keyof NavigateProps | keyof WithRouterProps>) => {
    const navigate = useNavigate();
    const location = useLocation();
    const params = useParams();
    const [searchParams, setSearchParams] = useSearchParams();

    // Build navigation props
    const navigationProps: NavigateProps = {
      navigate,
      location,
      params,
      searchParams,
      setSearchParams
    };

    // If legacy mode, also provide v5-compatible history and match objects
    if (options?.legacyMode) {
      const legacyProps: WithRouterProps = {
        ...navigationProps,
        history: {
          push: (path: string, state?: unknown) => navigate(path, { state }),
          replace: (path: string, state?: unknown) => navigate(path, { replace: true, state }),
          go: (delta: number) => navigate(delta),
          goBack: () => navigate(-1),
          goForward: () => navigate(1),
          location
        },
        match: {
          params,
          path: location.pathname,
          url: location.pathname
        }
      };

      return <WrappedComponent {...(props as P)} {...legacyProps} />;
    }

    return <WrappedComponent {...(props as P)} {...navigationProps} />;
  };

  WithNavigateComponent.displayName = `withNavigate(${WrappedComponent.displayName || WrappedComponent.name || "Component"})`;

  return WithNavigateComponent;
}

/**
 * Hook to get navigation utilities (for function components).
 * Provides both v6 navigate and v5-compatible history API.
 */
export function useNavigation() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  // V5-compatible history object
  const history = {
    push: (path: string, state?: unknown) => navigate(path, { state }),
    replace: (path: string, state?: unknown) => navigate(path, { replace: true, state }),
    go: (delta: number) => navigate(delta),
    goBack: () => navigate(-1),
    goForward: () => navigate(1),
    location
  };

  return {
    navigate,
    location,
    params,
    searchParams,
    setSearchParams,
    history // Legacy compatibility
  };
}

export default withNavigate;
